import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter } from '@medplum/fhirtypes';
import type { PortalAppointment } from '../../../pages/client-portal/data/mockClientPortalData';
import {
  mockUpcomingAppointments,
  mockPastAppointments,
} from '../../../pages/client-portal/data/mockClientPortalData';
import {
  eocDisplayName,
  practitionerIdFromRef,
} from '../../../pages/client-portal/utils/portalEoc';
import { isFhirAppointmentUpcoming } from '@/lib/appointmentEffectiveEnd';
import {
  CLIENT_ABSENT_ATTENDANCE_STATUSES,
  normalizeCalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';
import {
  getParticipantAttendanceStatus,
  getVideoMeetingUrl,
} from '@unify/fhir-model/resources/appointment';
import {
  loadPractitionerPreferredLabelMap,
  preferredLabelOrFallback,
} from '@/lib/loadPractitionerPreferredLabels';
import { countActivePatientParticipants } from '../../../pages/client-portal/utils/portalAppointmentCancel';

/** Statuses included in the near-term / upcoming calendar window search. */
const PORTAL_IN_WINDOW_APPOINTMENT_STATUSES =
  'booked,pending,proposed,arrived,checked-in,cancelled,noshow';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface AppointmentsDataConfig {
  /** When true, fetch live Appointment resources from Medplum. */
  live: boolean;
  /** Medplum client instance (required when live=true). */
  medplum?: MedplumClient;
  /**
   * When set, restricts search to this Patient (primary/dependent). Required for guarantor proxy
   * logins so results match the portal-subject patient, not the guarantor’s own Patient record.
   */
  patientId?: string;
}

export interface AppointmentsPageData {
  upcoming: PortalAppointment[];
  past: PortalAppointment[];
  source: 'mock' | 'medplum';
}

// ---------------------------------------------------------------------------
// Medplum → PortalAppointment normalizer
// ---------------------------------------------------------------------------

function mapStatus(fhirStatus: Appointment['status']): PortalAppointment['status'] {
  switch (fhirStatus) {
    case 'booked':
    case 'pending':
      return 'Scheduled';
    case 'proposed':
      return 'Requested';
    case 'fulfilled':
    case 'checked-in':
    case 'arrived':
      return 'Completed';
    case 'cancelled':
    case 'noshow':
    case 'entered-in-error':
      return 'Cancelled';
    default:
      return 'Scheduled';
  }
}

/** True when this portal patient has already cancelled / no-showed their own participant row. */
function isPortalActorAbsent(apt: Appointment, patientId: string | undefined): boolean {
  if (!patientId) return false;
  const patientRef = `Patient/${patientId}`;
  const participant = apt.participant?.find((p) => p.actor?.reference === patientRef);
  if (!participant) return false;
  const normalized = normalizeCalendarClientAttendanceStatus(
    getParticipantAttendanceStatus(participant),
  );
  return normalized != null && CLIENT_ABSENT_ATTENDANCE_STATUSES.has(normalized);
}

function resolveModality(
  apt: Appointment
): PortalAppointment['modality'] {
  const virtualCoding = apt.appointmentType?.coding?.find(
    (c) => c.code === 'VR' || c.display?.toLowerCase().includes('telehealth')
  );
  if (virtualCoding) return 'Telehealth';

  const virtualClass = (apt as Appointment & { class?: Encounter['class'] }).class;
  if (virtualClass?.code === 'VR') return 'Telehealth';

  return 'In person';
}

function normalizeAppointment(
  apt: Appointment,
  patientId: string | undefined,
  practitionerLabels: Map<string, string>,
): PortalAppointment {
  const practitionerParticipant = apt.participant?.find((p) =>
    p.actor?.reference?.startsWith('Practitioner/')
  );
  const providerRef = practitionerParticipant?.actor?.reference;
  const providerId = providerRef ? practitionerIdFromRef(providerRef) : undefined;
  const modality = resolveModality(apt);
  const status = isPortalActorAbsent(apt, patientId) ? 'Cancelled' : mapStatus(apt.status);

  return {
    id: apt.id ?? crypto.randomUUID(),
    start: apt.start ?? new Date().toISOString(),
    end: apt.end ?? new Date().toISOString(),
    serviceType:
      apt.serviceType?.[0]?.coding?.[0]?.display ??
      apt.serviceType?.[0]?.text ??
      'Appointment',
    providerLabel: preferredLabelOrFallback(
      providerId,
      practitionerLabels,
      practitionerParticipant?.actor?.display,
    ),
    ...(providerId ? { providerId } : {}),
    modality,
    status,
    locationLabel: modality === 'Telehealth' ? 'Telehealth' : 'Clinic',
    joinUrl: modality === 'Telehealth' ? `/client-portal/session/${apt.id}/join` : undefined,
    videoMeetingUrl: getVideoMeetingUrl(apt),
    activePatientParticipantCount: countActivePatientParticipants(apt),
  };
}

/** Cancelled visits belong in Past even when the slot is still in the future. */
function isPortalPastAppointment(
  apt: Appointment,
  normalized: PortalAppointment,
  nowMs: number,
): boolean {
  if (normalized.status === 'Cancelled') return true;
  return !isFhirAppointmentUpcoming(apt, nowMs);
}

async function enrichAppointmentsWithEpisodeOfCare(
  medplum: MedplumClient,
  patientId: string | undefined,
  appointments: PortalAppointment[]
): Promise<PortalAppointment[]> {
  if (!patientId || appointments.length === 0) return appointments;
  try {
    const eocs = await medplum.searchResources('EpisodeOfCare', {
      patient: `Patient/${patientId}`,
      _count: '100',
    });
    return appointments.map((apt) => {
      if (apt.episodeOfCareId && apt.episodeLabel) return apt;
      const pid = apt.providerId;
      if (!pid) return apt;
      const match = eocs.find(
        (e) => practitionerIdFromRef(e.careManager?.reference) === pid
      );
      if (!match?.id) return apt;
      return {
        ...apt,
        episodeOfCareId: match.id,
        episodeLabel: eocDisplayName(match),
      };
    });
  } catch {
    return appointments;
  }
}

// ---------------------------------------------------------------------------
// Data fetcher
// ---------------------------------------------------------------------------

/**
 * Fetch appointments page data.
 *
 * - In mock mode, returns the existing mock data unchanged.
 * - In live mode, queries Medplum for Appointment resources visible to the
 *   authenticated user (scoped by AccessPolicy on the server), normalizes
 *   them into PortalAppointment shape, and splits into upcoming/past.
 *
 * Server-side AccessPolicies enforce that:
 * - Patients see only their own appointments
 * - Providers see appointments for their panel
 * - Supervisors see appointments for supervised panels
 */
export async function getAppointmentsPageData(
  cfg: AppointmentsDataConfig
): Promise<AppointmentsPageData> {
  if (!cfg.live || !cfg.medplum) {
    return {
      upcoming: [...mockUpcomingAppointments],
      past: [...mockPastAppointments],
      source: 'mock',
    };
  }

  try {
    const now = new Date().toISOString();
    const nowMs = Date.now();
    const lookbackMs = 6 * 60 * 60 * 1000;
    const lookback = new Date(nowMs - lookbackMs).toISOString();
    const patientFilter = cfg.patientId ? { patient: `Patient/${cfg.patientId}` } : {};

    const [inWindowBundle, pastStartBundle] = await Promise.all([
      cfg.medplum.searchResources('Appointment', {
        ...patientFilter,
        date: `ge${lookback}`,
        status: PORTAL_IN_WINDOW_APPOINTMENT_STATUSES,
        _sort: 'date',
        _count: '100',
      }),
      cfg.medplum.searchResources('Appointment', {
        ...patientFilter,
        'date:missing': 'false',
        date: `lt${now}`,
        _sort: '-date',
        _count: '50',
      }),
    ]);

    const merged = new Map<string, Appointment>();
    for (const apt of [...inWindowBundle, ...pastStartBundle]) {
      if (apt.id) merged.set(apt.id, apt);
    }

    const upcomingNorm: PortalAppointment[] = [];
    const pastNorm: PortalAppointment[] = [];
    const practitionerLabels = await loadPractitionerPreferredLabelMap(
      cfg.medplum,
      [...merged.values()].flatMap((apt) => {
        const id = practitionerIdFromRef(
          apt.participant?.find((p) => p.actor?.reference?.startsWith('Practitioner/'))?.actor?.reference,
        );
        return id ? [id] : [];
      }),
    );
    for (const apt of merged.values()) {
      const normalized = normalizeAppointment(apt, cfg.patientId, practitionerLabels);
      if (isPortalPastAppointment(apt, normalized, nowMs)) {
        pastNorm.push(normalized);
      } else {
        upcomingNorm.push(normalized);
      }
    }

    upcomingNorm.sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    );
    pastNorm.sort(
      (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime(),
    );
    const [upcoming, past] = await Promise.all([
      enrichAppointmentsWithEpisodeOfCare(cfg.medplum, cfg.patientId, upcomingNorm),
      enrichAppointmentsWithEpisodeOfCare(cfg.medplum, cfg.patientId, pastNorm),
    ]);

    return {
      upcoming,
      past,
      source: 'medplum',
    };
  } catch (err) {
    console.error('[appointments] Live fetch failed:', err);
    return {
      upcoming: [],
      past: [],
      source: 'medplum',
    };
  }
}
